import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { t, font } from "@realtyflows/ui";
import useStorageUsage, { formatBytes } from "../hooks/useStorageUsage";

const Icon = ({ paths, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {paths}
  </svg>
);

const icons = {
  dashboard: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></>,
  clients: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  contracts: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></>,
  deals: <><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></>,
  messages: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></>,
  emails: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>,
  appointments: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  automations: <><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>,
  team: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
  pricing: <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>,
};

function NavBtn({ iconKey, label, active, collapsed, onClick, badge, hasChildren, expanded }) {
  const [hovered, setHovered] = useState(false);
  const isHighlight = active || hovered;
  return (
    <div style={{ position: "relative" }}>
      <button className="rf-sitem" onClick={onClick} title={collapsed ? label : undefined}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start",
          gap: 12, padding: collapsed ? "11px 0" : "11px 20px",
          marginBottom: 1, cursor: "pointer", textAlign: "left",
          background: active ? `${t.gold}12` : hovered ? "rgba(255,255,255,0.08)" : "transparent",
          border: "none", borderLeft: `3px solid ${active ? t.gold : "transparent"}`,
          transition: "all 0.15s", fontFamily: font.body,
          color: isHighlight ? t.goldLight : "rgba(255,255,255,0.6)",
        }}>
        <span style={{ flexShrink: 0, display: "flex", alignItems: "center", opacity: isHighlight ? 1 : 0.7 }}><Icon paths={icons[iconKey]} /></span>
        {!collapsed && (
          <>
            <span style={{ fontSize: 12, fontWeight: isHighlight ? 600 : 500, color: isHighlight ? t.goldLight : "rgba(255,255,255,0.6)", whiteSpace: "nowrap", flex: 1, textAlign: "left" }}>{label}</span>
            {hasChildren && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0, transition: "transform 0.2s ease", transform: expanded ? "rotate(180deg)" : "rotate(0)" }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </>
        )}
      </button>
      {badge && !collapsed && (
        <div style={{ position: "absolute", right: hasChildren ? 32 : 16, top: "50%", transform: "translateY(-50%)", background: t.gold, color: t.navy, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10, minWidth: 20, textAlign: "center" }}>{badge}</div>
      )}
      {badge && collapsed && (
        <div style={{ position: "absolute", top: 6, right: 8, width: 8, height: 8, borderRadius: "50%", background: t.gold }} />
      )}
    </div>
  );
}

function SubNavBtn({ label, active, onClick, collapsed }) {
  const [hovered, setHovered] = useState(false);
  if (collapsed) return null;
  const isHighlight = active || hovered;
  return (
    <button className="rf-sitem" onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "100%", display: "flex", alignItems: "center",
        gap: 8, padding: "8px 20px 8px 48px",
        marginBottom: 1, cursor: "pointer", textAlign: "left",
        background: active ? `${t.gold}08` : hovered ? "rgba(255,255,255,0.06)" : "transparent",
        border: "none", borderLeft: `3px solid ${active ? `${t.gold}80` : "transparent"}`,
        transition: "all 0.15s", fontFamily: font.body,
        color: isHighlight ? t.goldLight : "rgba(255,255,255,0.45)",
      }}>
      <div style={{
        width: 4, height: 4, borderRadius: "50%", flexShrink: 0,
        background: isHighlight ? t.gold : "rgba(255,255,255,0.25)",
      }} />
      <span style={{ fontSize: 11, fontWeight: isHighlight ? 600 : 400, whiteSpace: "nowrap", textAlign: "left" }}>{label}</span>
    </button>
  );
}

export default function Sidebar({ user, plan, role, onLogout, collapsed, onToggle, hideToggle, emailUnread, trialInfo, activeWorkspace, onWorkspaceChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const storage = useStorageUsage();
  const currentPath = "/" + location.pathname.split("/")[1];
  const fullPath = location.pathname;

  // Track which parent items are expanded
  const [expanded, setExpanded] = useState(() => {
    // Auto-expand if currently on a sub-route
    const open = {};
    if (fullPath.startsWith("/settings")) open["/settings"] = true;
    if (fullPath.startsWith("/deals")) open["/deals"] = true;
    return open;
  });

  const toggleExpand = (path) => {
    setExpanded(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const goTo = (path) => {
    if (location.pathname === path) {
      navigate("/", { replace: true });
      setTimeout(() => { navigate(path, { replace: true }); window.scrollTo(0, 0); }, 0);
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  const tierLevel = { starter: 1, pro: 2, team_starter: 3, team_pro: 4, agency: 5, enterprise: 6 };
  const isPro = (tierLevel[plan] || 0) >= 2;

  const tabs = [
    { path: "/dashboard", label: "Dashboard", iconKey: "dashboard" },
    { path: "/contracts", label: "Contracts", iconKey: "contracts" },
    { path: "/clients", label: "Clients", iconKey: "clients" },
    {
      path: "/deals", label: "Deals", iconKey: "deals",
      children: [
        { path: "/deals", label: "Pipeline", exact: true },
        { path: "/deals/pipelines", label: "Pipeline Settings" },
      ],
    },
    { path: "/emails", label: "Emails", iconKey: "emails", badge: emailUnread || undefined },
    { path: "/appointments", label: "Appointments", iconKey: "appointments" },
    { path: "/team", label: "Team", iconKey: "team" },
    {
      path: "/settings", label: "Settings", iconKey: "settings",
      children: [
        { path: "/settings", label: "General", exact: true },
        { path: "/settings/automations", label: "Automations" },
      ],
    },
  ];

  const w = collapsed ? 60 : 220;

  return (
    <>
    {/* Spacer to push main content right */}
    <div style={{ width: w, minWidth: w, flexShrink: 0, transition: "width 0.2s ease, min-width 0.2s ease" }} />
    <aside className="rf-sidebar" style={{
      width: w,
      position: "fixed", top: 0, left: 0, bottom: 0,
      background: t.navyLight, borderRight: `1px solid rgba(255,255,255,0.06)`,
      fontFamily: font.body, transition: "width 0.2s ease",
      display: "flex", flexDirection: "column",
      overflowX: "hidden", overflowY: "hidden",
      zIndex: 20,
    }}>
      {/* Toggle button */}
      <button onClick={onToggle} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        style={{
          position: "fixed", top: 28, left: w - 14, zIndex: 30,
          background: t.surface, border: `1.5px solid ${t.border}`, cursor: "pointer",
          width: 28, height: 28, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 1px 4px rgba(0,0,0,0.1)", transition: "left 0.2s ease, background 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
        onMouseLeave={e => e.currentTarget.style.background = t.surface}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textPrimary} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          {collapsed ? <polyline points="9 6 15 12 9 18" /> : <polyline points="15 6 9 12 15 18" />}
        </svg>
      </button>
        {/* Logo */}
        <div style={{ padding: collapsed ? "20px 0" : "20px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => goTo("/dashboard")}>
            <img src="/logo.png" alt="RealtyFlows" style={{ width: 24, height: 24, flexShrink: 0, borderRadius: 4 }} />
            {!collapsed && (
              <div>
                <span style={{ fontSize: 18, fontWeight: 600, color: "#fff", fontFamily: font.display, whiteSpace: "nowrap" }}>
                  Realty<span style={{ color: t.gold }}>Flows</span>
                </span>
                <div style={{ fontSize: 8, color: "rgba(255,255,255,0.35)", letterSpacing: "2px", fontFamily: font.body, textTransform: "uppercase", fontWeight: 700 }}>AI REAL ESTATE CRM</div>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable middle section */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", overscrollBehavior: "contain" }}>

        {/* Free Plan Banner — top of sidebar */}
        {trialInfo?.isFree && !collapsed && (
          <div style={{
            margin: "12px 12px 0", padding: "12px 14px 10px",
            background: "rgba(255,255,255,0.06)", borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.1)",
          }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: t.gold, marginBottom: 8, fontFamily: font.body }}>FREE PLAN</div>
            <div style={{ textAlign: "center", background: (trialInfo.freeSigningsRemaining ?? 0) > 0 ? `${t.gold}15` : "rgba(239,68,68,0.15)", borderRadius: 6, padding: "6px 0", marginBottom: 8, border: `1px solid ${(trialInfo.freeSigningsRemaining ?? 0) > 0 ? `${t.gold}30` : "rgba(239,68,68,0.3)"}` }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: (trialInfo.freeSigningsRemaining ?? 0) > 0 ? t.gold : "#ef4444", fontFamily: font.mono }}>{trialInfo.freeSigningsRemaining ?? 0}</div>
              <div style={{ fontSize: 7, color: "rgba(255,255,255,0.4)", fontFamily: font.body, letterSpacing: "0.5px" }}>FREE SIGNING{(trialInfo.freeSigningsRemaining ?? 0) !== 1 ? "S" : ""}</div>
            </div>
            <button onClick={() => navigate("/settings")} style={{
              width: "100%", padding: "6px 0", fontSize: 9, fontWeight: 700,
              letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer",
              background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight || t.gold})`,
              border: "none", color: "#fff", borderRadius: 6, fontFamily: font.body,
            }}>Subscribe for More</button>
          </div>
        )}

        {/* Signings counter — for paid users */}
        {trialInfo && !trialInfo.isFree && trialInfo.subscriptionStatus === "active" && !collapsed && (
          <div style={{
            margin: "12px 12px 0", padding: "10px 14px",
            background: "rgba(255,255,255,0.06)", borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.1)",
          }}>
            {/* Plan signings */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: t.gold, fontFamily: font.body }}>E-SIGNINGS</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", fontFamily: font.mono }}>
                {Math.max(0, (trialInfo.signingsLimit || 0) - (trialInfo.signingsUsed || 0))} / {trialInfo.signingsLimit || 0}
              </div>
            </div>
            <div style={{ height: 3, borderRadius: 2, background: "rgba(255,255,255,0.1)", overflow: "hidden", marginBottom: 4 }}>
              <div style={{
                height: "100%", borderRadius: 2, transition: "width 0.3s",
                background: ((trialInfo.signingsUsed || 0) / (trialInfo.signingsLimit || 1)) >= 0.8 ? "#ef4444" : t.gold,
                width: `${Math.min(100, ((trialInfo.signingsUsed || 0) / (trialInfo.signingsLimit || 1)) * 100)}%`,
              }} />
            </div>
            {trialInfo.signingsResetDate && (
              <div style={{ fontSize: 8, color: "rgba(255,255,255,0.35)", fontFamily: font.body, marginBottom: 8 }}>
                Resets {new Date(trialInfo.signingsResetDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </div>
            )}
            {/* Addon signings */}
            {(trialInfo.addonBalance || 0) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: t.emerald, fontFamily: font.body }}>PURCHASED</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", fontFamily: font.mono }}>
                  {trialInfo.addonBalance} <span style={{ fontSize: 7, color: "rgba(255,255,255,0.3)" }}>no expiry</span>
                </div>
              </div>
            )}
            {/* Total */}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 6, marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.6)", fontFamily: font.body }}>TOTAL</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: font.mono }}>
                {trialInfo.totalSigningsAvailable ?? (Math.max(0, (trialInfo.signingsLimit || 0) - (trialInfo.signingsUsed || 0)) + (trialInfo.addonBalance || 0))} left
              </div>
            </div>
            {(trialInfo.totalSigningsAvailable ?? 999) <= 2 && (
              <button onClick={() => navigate("/settings")} style={{
                width: "100%", marginTop: 8, padding: "5px 0", fontSize: 9, fontWeight: 700,
                letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer",
                background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight || t.gold})`,
                border: "none", color: "#fff", borderRadius: 6, fontFamily: font.body,
              }}>Buy More Signings</button>
            )}
          </div>
        )}

        {/* Plan cancelling warning */}
        {trialInfo?.subscriptionStatus === "cancelling" && !collapsed && (
          <div style={{
            margin: "12px 12px 0", padding: "10px 14px",
            background: "rgba(202,138,4,0.1)", borderRadius: 10,
            border: "1px solid rgba(202,138,4,0.3)",
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#ca8a04", marginBottom: 4, fontFamily: font.body }}>Plan Ending Soon</div>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", marginBottom: 8, fontFamily: font.body }}>Your plan will cancel at the end of your billing period.</div>
            <button onClick={() => navigate("/billing?tab=payment")} style={{
              width: "100%", padding: "5px 0", fontSize: 9, fontWeight: 700,
              letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer",
              background: "#ca8a04", border: "none", color: "#fff", borderRadius: 6, fontFamily: font.body,
            }}>Resume Plan</button>
          </div>
        )}

        {/* Cancelled — reactivate */}
        {trialInfo?.subscriptionStatus === "cancelled" && !collapsed && (
          <div style={{
            margin: "12px 12px 0", padding: "10px 14px",
            background: "rgba(239,68,68,0.1)", borderRadius: 10,
            border: "1px solid rgba(239,68,68,0.3)",
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#ef4444", marginBottom: 4, fontFamily: font.body }}>Plan Cancelled</div>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", marginBottom: 8, fontFamily: font.body }}>Reactivate to access all features and signings.</div>
            <button onClick={() => navigate("/settings")} style={{
              width: "100%", padding: "5px 0", fontSize: 9, fontWeight: 700,
              letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer",
              background: "#ef4444", border: "none", color: "#fff", borderRadius: 6, fontFamily: font.body,
            }}>Reactivate</button>
          </div>
        )}


        {/* Storage usage */}
        {!storage.loading && !collapsed && (
          <div style={{
            margin: "12px 12px 0", padding: "10px 14px",
            background: "rgba(255,255,255,0.06)", borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.1)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: t.emerald, fontFamily: font.body }}>STORAGE</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", fontFamily: font.mono }}>
                {storage.usedGB < 0.1 ? formatBytes(storage.usedBytes) : storage.usedGB.toFixed(1) + " GB"} / {storage.limitGB < 1 ? formatBytes(storage.limitBytes) : storage.limitGB.toFixed(0) + " GB"}
              </div>
            </div>
            <div style={{ height: 3, borderRadius: 2, background: "rgba(255,255,255,0.1)", overflow: "hidden", marginBottom: 4 }}>
              <div style={{
                height: "100%", borderRadius: 2, transition: "width 0.3s",
                background: storage.isNearLimit ? (storage.isAtLimit ? "#ef4444" : t.gold) : t.emerald,
                width: `${Math.min(100, storage.percentUsed)}%`,
              }} />
            </div>
            {storage.isNearLimit && (
              <div style={{ fontSize: 8, color: storage.isAtLimit ? "#ef4444" : t.gold, fontFamily: font.body }}>
                {storage.isAtLimit ? "Storage full" : "Storage almost full"}
              </div>
            )}
          </div>
        )}
        {!storage.loading && collapsed && (
          <div title={`Storage: ${formatBytes(storage.usedBytes)} / ${formatBytes(storage.limitBytes)}`}
            style={{ margin: "12px 8px 0", padding: "6px 0" }}>
            <div style={{ height: 3, borderRadius: 2, background: "rgba(255,255,255,0.1)", overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 2,
                background: storage.isNearLimit ? (storage.isAtLimit ? "#ef4444" : t.gold) : t.emerald,
                width: `${Math.min(100, storage.percentUsed)}%`,
              }} />
            </div>
          </div>
        )}

        {/* Section label */}
        {!collapsed && <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", padding: "20px 20px 8px" }}>Navigation</div>}

        {/* Nav items */}
        <div style={{ padding: collapsed ? "4px 6px" : "0" }}>
          {tabs.map(tab => {
            const hasChildren = tab.children && tab.children.length > 0;
            const isParentActive = currentPath === tab.path;
            const isExpanded = !!expanded[tab.path];

            return (
              <div key={tab.path}>
                <NavBtn
                  iconKey={tab.iconKey}
                  label={tab.label}
                  active={isParentActive}
                  collapsed={collapsed}
                  badge={tab.badge}
                  hasChildren={hasChildren}
                  expanded={isExpanded}
                  onClick={() => {
                    if (hasChildren) {
                      if (!isParentActive) {
                        // Navigate to first child and expand
                        goTo(tab.children[0].path);
                        setExpanded(prev => ({ ...prev, [tab.path]: true }));
                      } else {
                        // Already on this section, just toggle
                        toggleExpand(tab.path);
                      }
                    } else {
                      goTo(tab.path);
                    }
                  }}
                />
                {/* Sub-items with slide animation */}
                {hasChildren && (
                  <div style={{
                    overflow: "hidden",
                    maxHeight: isExpanded && !collapsed ? tab.children.length * 36 : 0,
                    opacity: isExpanded && !collapsed ? 1 : 0,
                    transition: "max-height 0.25s ease, opacity 0.2s ease",
                  }}>
                    {tab.children.map(child => {
                      const isChildActive = child.exact
                        ? fullPath === child.path
                        : fullPath === child.path || fullPath.startsWith(child.path + "/");
                      return (
                        <SubNavBtn
                          key={child.path}
                          label={child.label}
                          active={isChildActive}
                          collapsed={collapsed}
                          onClick={() => goTo(child.path)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        </div>{/* end scrollable middle */}

        {/* Logout at bottom — pinned */}
        <div style={{ flexShrink: 0, borderTop: "1px solid rgba(255,255,255,0.08)", padding: collapsed ? "4px 6px" : "0" }}>
          <NavBtn iconKey="logout" label="Logout" active={false} collapsed={collapsed} onClick={onLogout} />
        </div>
    </aside>
    </>
  );
}
